import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit, Image, Plus, RefreshCw, Trash2, Trophy, Upload, Users, ZoomIn, CircleDot, Swords, Target } from 'lucide-react';
import { api } from '@/api';
import { resolveMediaUrl } from '@/utils/helpers';
import { MediaPickerDialog } from './AdminShared';

// Reliable sport icon mapping (emojis render as text on some devices)
const SPORT_ICONS = {
  'soccer': CircleDot,
  'rugby': Swords,
  'gaelic-football': Target,
  'hurling-camogie': Swords,
};

const SportIcon = ({ sport, className = "w-5 h-5" }) => {
  const IconComponent = SPORT_ICONS[sport?.slug] || Trophy;
  return <IconComponent className={className} />;
};

const AdminPartners = ({ partners, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    logo: '',
    website: '',
    category: 'partner',
    color: 'sky',
    order: 0,
    is_active: true
  });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  const colorOptions = [
    { value: 'sky', label: 'Sky Blue', class: 'bg-sky-500' },
    { value: 'green', label: 'Green', class: 'bg-green-500' },
    { value: 'red', label: 'Red', class: 'bg-red-500' },
    { value: 'purple', label: 'Purple', class: 'bg-purple-500' },
    { value: 'yellow', label: 'Yellow', class: 'bg-yellow-500' },
    { value: 'pink', label: 'Pink', class: 'bg-pink-500' },
    { value: 'orange', label: 'Orange', class: 'bg-orange-500' },
  ];

  const categoryOptions = [
    { value: 'partner', label: 'Partner' },
    { value: 'affiliate', label: 'Affiliate' },
    { value: 'sponsor', label: 'Sponsor' },
    { value: 'league', label: 'Conference Partner' },
  ];

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, logo: res.data.url });
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      logo: '',
      website: '',
      category: 'partner',
      color: 'sky',
      order: 0,
      is_active: true
    });
    setEditingPartner(null);
    setShowForm(false);
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setFormData({
      name: partner.name,
      description: partner.description,
      logo: partner.logo || '',
      website: partner.website || '',
      category: partner.category || 'partner',
      color: partner.color || 'sky',
      order: partner.order || 0,
      is_active: partner.is_active !== false
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPartner) {
        await api.updatePartner(editingPartner.id, formData);
      } else {
        await api.createPartner(formData);
      }
      resetForm();
      onRefresh();
    } catch (error) {
      console.error('Error saving partner:', error);
      alert('Failed to save partner');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this partner?')) return;
    try {
      await api.deletePartner(id);
      onRefresh();
    } catch (error) {
      console.error('Error deleting partner:', error);
      alert('Failed to delete partner');
    }
  };

  const getColorClass = (color, opacity = '500') => {
    const colors = {
      sky: `bg-sky-${opacity}`,
      green: `bg-green-${opacity}`,
      red: `bg-red-${opacity}`,
      purple: `bg-purple-${opacity}`,
      yellow: `bg-yellow-${opacity}`,
      pink: `bg-pink-${opacity}`,
      orange: `bg-orange-${opacity}`,
    };
    return colors[color] || colors.sky;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Partners & Affiliations</h2>
        <Button 
          onClick={() => setShowForm(true)} 
          className="bg-sky-400 hover:bg-sky-500"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Partner
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingPartner ? 'Edit Partner' : 'Add New Partner'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Partner Name *</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., New York GAA"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Website URL</Label>
                  <Input 
                    value={formData.website} 
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="https://..."
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Description *</Label>
                <textarea 
                  value={formData.description} 
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2 min-h-[80px]"
                  placeholder="Brief description of the partnership..."
                  required
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-300">Category</Label>
                  <select 
                    value={formData.category} 
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
                  >
                    {categoryOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Display Color</Label>
                  <select 
                    value={formData.color} 
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                    className="w-full bg-gray-700 border-gray-600 text-white rounded-md p-2"
                  >
                    {colorOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-gray-300">Display Order</Label>
                  <Input 
                    type="number" 
                    value={formData.order} 
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="bg-gray-700 border-gray-600 text-white"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-gray-300">Logo</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.logo} 
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="URL or upload/browse"
                  />
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    className="border-gray-600"
                    onClick={() => setShowMediaPicker(true)}
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".png,.svg,.webp,.jpg,.jpeg,image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                    </span>
                  </label>
                  {formData.logo && (
                    <img 
                      src={resolveMediaUrl(formData.logo)} 
                      alt="Logo preview" 
                      className="h-12 w-12 object-contain bg-gray-700 rounded p-1"
                    />
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <input 
                  type="checkbox" 
                  checked={formData.is_active} 
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded"
                  id="is_active"
                />
                <Label htmlFor="is_active" className="text-gray-300">Active (visible on About Us page)</Label>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400 text-gray-900">
                  {editingPartner ? 'Update Partner' : 'Add Partner'}
                </Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Partners List */}
      <div className="grid gap-4">
        {partners.length === 0 ? (
          <Card className="bg-gray-800/50 border-gray-700">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-500" />
              <p className="text-gray-400">No partners added yet. Click &quot;Add Partner&quot; to create your first one.</p>
            </CardContent>
          </Card>
        ) : (
          partners.map(partner => (
            <Card key={partner.id} className={`bg-gray-800/50 border-gray-700 ${!partner.is_active ? 'opacity-50' : ''}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {partner.logo ? (
                    <div className={`w-14 h-14 rounded-lg ${getColorClass(partner.color, '500')}/20 p-2 flex items-center justify-center`}>
                      <img src={resolveMediaUrl(partner.logo)} alt={partner.name} className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className={`w-14 h-14 rounded-lg ${getColorClass(partner.color, '500')}/20 flex items-center justify-center`}>
                      <Trophy className={`w-7 h-7 text-${partner.color}-400`} />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold">{partner.name}</h3>
                      {!partner.is_active && (
                        <span className="px-2 py-0.5 bg-gray-600 text-gray-300 rounded text-xs">Inactive</span>
                      )}
                      <span className="px-2 py-0.5 bg-gray-700 text-gray-400 rounded text-xs capitalize">{partner.category}</span>
                    </div>
                    <p className="text-sm text-gray-400 line-clamp-1">{partner.description}</p>
                    {partner.website && (
                      <a href={partner.website} target="_blank" rel="noopener noreferrer" className="text-xs text-sky-400 hover:underline">
                        {partner.website}
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => handleEdit(partner)}>
                    <Edit className="w-4 h-4 text-gray-400" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(partner.id)}>
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

// Admin Page Banners Management
const AdminPageBanners = ({ onRefresh }) => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    hero_image: '',
    hero_title: '',
    hero_subtitle: '',
    overlay_opacity: 0.5,
    image_position: 'center',
    image_zoom: 100,
    image_position_x: 50,
    image_position_y: 50,
    is_active: true
  });
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  const defaultPages = [
    { page_slug: 'home', page_name: 'Home' },
    { page_slug: 'about', page_name: 'About Us' },
    { page_slug: 'scores', page_name: 'Live Scores' },
    { page_slug: 'fixtures', page_name: 'Fixtures' },
    { page_slug: 'sport-soccer', page_name: 'Soccer' },
    { page_slug: 'sport-gaelic-football', page_name: 'Gaelic Football' },
    { page_slug: 'sport-hurling-camogie', page_name: 'Hurling/Camogie' },
    { page_slug: 'sport-rugby', page_name: 'Rugby' },
  ];

  useEffect(() => {
    loadBanners();
  }, []);

  const loadBanners = async () => {
    try {
      // First init default banners
      await api.initPageBanners();
      const res = await api.getPageBanners();
      setBanners(res.data);
    } catch (error) {
      console.error('Error loading page banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({
      hero_image: banner.hero_image || '',
      hero_title: banner.hero_title || '',
      hero_subtitle: banner.hero_subtitle || '',
      overlay_opacity: banner.overlay_opacity || 0.5,
      image_position: banner.image_position || 'center',
      image_zoom: banner.image_zoom || 100,
      image_position_x: banner.image_position_x ?? 50,
      image_position_y: banner.image_position_y ?? 50,
      is_active: banner.is_active !== false
    });
  };

  const handleSave = async () => {
    if (!editingBanner) return;
    setSaving(true);
    try {
      await api.updatePageBanner(editingBanner.page_slug, formData);
      await loadBanners();
      setEditingBanner(null);
      setFormData({ hero_image: '', hero_title: '', hero_subtitle: '', overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true });
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Failed to save banner');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingBanner(null);
    setFormData({ hero_image: '', hero_title: '', hero_subtitle: '', overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true });
  };

  const resetImageTransform = () => {
    setFormData({ ...formData, image_zoom: 100, image_position_x: 50, image_position_y: 50 });
  };

  // Merge default pages with existing banners
  const allPages = defaultPages.map(page => {
    const existing = banners.find(b => b.page_slug === page.page_slug);
    return existing || { ...page, hero_image: null, hero_title: null, hero_subtitle: null, overlay_opacity: 0.5, image_position: 'center', image_zoom: 100, image_position_x: 50, image_position_y: 50, is_active: true };
  });

  if (loading) {
    return <div className="text-gray-400 text-center py-8">Loading page banners...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Page Banners</h2>
          <p className="text-gray-400 text-sm mt-1">Set hero images for each page of your site</p>
        </div>
      </div>

      {/* Edit Form */}
      {editingBanner && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">Edit Banner: {editingBanner.page_name}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">Hero Image</Label>
              <div className="flex gap-2">
                <Input 
                  value={formData.hero_image} 
                  onChange={(e) => setFormData({ ...formData, hero_image: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white flex-1"
                  placeholder="Image URL or browse from Media Library"
                />
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline" 
                  className="border-gray-600"
                  onClick={() => setShowMediaPicker(true)}
                >
                  <Image className="w-4 h-4" />
                </Button>
              </div>
              {formData.hero_image && (
                <div className="mt-2 relative rounded-lg overflow-hidden" style={{ height: '120px' }}>
                  <img 
                    src={resolveMediaUrl(formData.hero_image)} 
                    alt="Preview" 
                    className="w-full h-full"
                    style={{
                      objectFit: 'cover',
                      objectPosition: `${formData.image_position_x}% ${formData.image_position_y}%`,
                      transform: `scale(${formData.image_zoom / 100})`,
                      transformOrigin: `${formData.image_position_x}% ${formData.image_position_y}%`
                    }}
                  />
                  <div className="absolute inset-0 bg-black" style={{ opacity: formData.overlay_opacity }} />
                  {formData.hero_title && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-white font-bold text-lg">{formData.hero_title}</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Title Overlay (optional)</Label>
                <Input 
                  value={formData.hero_title} 
                  onChange={(e) => setFormData({ ...formData, hero_title: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="e.g., About Us"
                />
              </div>
              <div>
                <Label className="text-gray-300">Subtitle (optional)</Label>
                <Input 
                  value={formData.hero_subtitle} 
                  onChange={(e) => setFormData({ ...formData, hero_subtitle: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white"
                  placeholder="e.g., Learn about our services"
                />
              </div>
            </div>

            <div>
              <Label className="text-gray-300">Overlay Darkness: {Math.round(formData.overlay_opacity * 100)}%</Label>
              <input 
                type="range" 
                min="0" 
                max="1" 
                step="0.1"
                value={formData.overlay_opacity}
                onChange={(e) => setFormData({ ...formData, overlay_opacity: parseFloat(e.target.value) })}
                className="w-full mt-2"
              />
            </div>

            <div>
              <Label className="text-gray-300">Image Vertical Alignment</Label>
              <div className="flex gap-2 mt-2">
                {['top', 'center', 'bottom'].map(pos => (
                  <Button
                    key={pos}
                    type="button"
                    variant={formData.image_position === pos ? "default" : "outline"}
                    className={formData.image_position === pos ? "bg-sky-500" : "border-gray-600 text-gray-300"}
                    onClick={() => setFormData({ ...formData, image_position: pos })}
                  >
                    {pos.charAt(0).toUpperCase() + pos.slice(1)}
                  </Button>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">Controls which part of the image is shown when cropped</p>
            </div>

            {/* Zoom & Pan Controls */}
            {formData.hero_image && (
              <div className="p-4 bg-gray-700/50 rounded-lg border border-gray-600 space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-white flex items-center gap-2">
                    <ZoomIn className="w-4 h-4 text-sky-400" />
                    Image Zoom & Position
                  </h4>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={resetImageTransform}
                    className="text-gray-400 hover:text-white text-xs"
                  >
                    <RefreshCw className="w-3 h-3 mr-1" /> Reset
                  </Button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Zoom: {formData.image_zoom}%</Label>
                    <input
                      type="range"
                      min="100"
                      max="200"
                      value={formData.image_zoom}
                      onChange={(e) => setFormData({ ...formData, image_zoom: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Horizontal: {formData.image_position_x}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.image_position_x}
                      onChange={(e) => setFormData({ ...formData, image_position_x: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 text-xs mb-1 block">Vertical: {formData.image_position_y}%</Label>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={formData.image_position_y}
                      onChange={(e) => setFormData({ ...formData, image_position_y: Number(e.target.value) })}
                      className="w-full accent-sky-500"
                    />
                  </div>
                </div>
                <p className="text-gray-500 text-xs">Adjust zoom and position to frame your hero image perfectly.</p>
              </div>
            )}

            <MediaPickerDialog
              open={showMediaPicker}
              onOpenChange={setShowMediaPicker}
              onSelect={(url) => setFormData({ ...formData, hero_image: url })}
            />

            <div className="flex gap-2">
              <Button onClick={handleSave} disabled={saving} className="bg-sky-300 hover:bg-sky-400 text-gray-900">
                {saving ? 'Saving...' : 'Save Banner'}
              </Button>
              <Button type="button" variant="outline" className="border-gray-600" onClick={handleCancel}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Pages List */}
      <div className="grid gap-4 md:grid-cols-2">
        {allPages.map(page => (
          <Card 
            key={page.page_slug} 
            className={`bg-gray-800/50 border-gray-700 cursor-pointer hover:border-sky-500/50 transition-colors ${editingBanner?.page_slug === page.page_slug ? 'border-sky-500' : ''}`}
            onClick={() => handleEdit(page)}
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                {/* Thumbnail Preview */}
                <div className="w-24 h-16 rounded-lg overflow-hidden bg-gray-700 flex-shrink-0">
                  {page.hero_image ? (
                    <img 
                      src={resolveMediaUrl(page.hero_image)} 
                      alt={page.page_name} 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-500">
                      <Image className="w-6 h-6" />
                    </div>
                  )}
                </div>
                {/* Info */}
                <div className="flex-1">
                  <h3 className="text-white font-semibold">{page.page_name}</h3>
                  <p className="text-sm text-gray-400">/{page.page_slug.replace('sport-', '').replace('about', 'about')}</p>
                  {page.hero_image ? (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-green-500/20 text-green-400 rounded text-xs">
                      Image Set
                    </span>
                  ) : (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-gray-600 text-gray-400 rounded text-xs">
                      No Image
                    </span>
                  )}
                </div>
                <Edit className="w-4 h-4 text-gray-500" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

// Admin Leagues Management
const REGIONS = [
  { value: 'west',    label: 'West' },
  { value: 'east',    label: 'East' },
  { value: 'central', label: 'Central' },
  { value: 'south',   label: 'South' },
];

const AdminLeagues = ({ leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingLeague, setEditingLeague] = useState(null);
  const [formData, setFormData] = useState({ sport_id: '', name: '', slug: '', logo: '', region: '', order: 0 });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Default sport_id to the first (Soccer) sport once loaded — single-sport WPSL TV
  React.useEffect(() => {
    if (!formData.sport_id && sports?.length > 0) {
      setFormData(f => ({ ...f, sport_id: sports[0].id }));
    }
  }, [sports]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    const validTypes = ['image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, SVG, or WebP file for transparent logos');
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', `${formData.name || 'League'} logo`);
      uploadFormData.append('tags', 'logo,league');
      
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const logoUrl = resolveMediaUrl(res.data.url);
      setFormData({ ...formData, logo: logoUrl });
    } catch (error) {
      alert('Error uploading logo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Build payload explicitly so no field can be silently dropped by stale closure
    const payload = {
      sport_id: formData.sport_id,
      name: formData.name,
      slug: formData.slug,
      logo: formData.logo || null,
      region: formData.region || null,
      order: parseInt(formData.order) || 0,
    };
    try {
      if (editingLeague) {
        await api.updateLeague(editingLeague.id, payload);
      } else {
        await api.createLeague(payload);
      }
      resetForm();
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error saving league: ${detail}`);
      console.error('Save league failed:', error);
    }
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingLeague(null);
    setFormData({ sport_id: sports?.[0]?.id || '', name: '', slug: '', logo: '', region: '', order: 0 });
  };

  const handleEdit = (league) => {
    setEditingLeague(league);
    setFormData({
      sport_id: league.sport_id,
      name: league.name,
      slug: league.slug,
      logo: league.logo || '',
      region: league.region || '',
      order: league.order || 0
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this league? All teams in this league will need to be reassigned.')) {
      try {
        await api.deleteLeague(id);
        onRefresh();
      } catch (error) {
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Error deleting league: ${detail}`);
        console.error('Delete league failed:', error);
      }
    }
  };

  const getSportName = (sportId) => sports.find(s => s.id === sportId)?.name || 'Unknown';
  const getSportIcon = (sportId) => sports.find(s => s.id === sportId)?.icon || '🏆';

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Conferences ({leagues.length})</h2>
          <p className="text-sm text-gray-400 mt-1">Sub-conferences grouped by WPSL region (West / East / Central / South)</p>
        </div>
        <Button
          onClick={() => { setShowForm(true); setEditingLeague(null); setFormData({ sport_id: sports?.[0]?.id || '', name: '', slug: '', logo: '', region: '', order: 0 }); }}
          className="bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-5 transition-colors"
          data-testid="add-conference-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Conference
        </Button>
      </div>

      {showForm && (
        <Card className="bg-gray-800/50 border-wpsl-blue/30">
          <CardHeader>
            <CardTitle className="text-white">{editingLeague ? 'Edit Conference' : 'Add New Conference'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Region</Label>
                  <Select value={formData.region || 'none'} onValueChange={(v) => setFormData(f => ({ ...f, region: v === 'none' ? '' : v }))}>
                    <SelectTrigger className="bg-gray-700 border-gray-600 text-white" data-testid="conference-region-select">
                      <SelectValue placeholder="Select Region" />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-700">
                      <SelectItem value="none" className="text-gray-300 hover:bg-gray-700">— None —</SelectItem>
                      {REGIONS.map(r => (
                        <SelectItem key={r.value} value={r.value} className="text-white hover:bg-gray-700">
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">Which WPSL region does this conference belong to?</p>
                </div>
                <div>
                  <Label className="text-gray-300">Conference Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData(f => ({ ...f, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., California North"
                    required
                    data-testid="conference-name-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Slug</Label>
                  <Input 
                    value={formData.slug} 
                    onChange={(e) => setFormData(f => ({ ...f, slug: e.target.value }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="california-north"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Display Order</Label>
                  <Input 
                    type="number"
                    value={formData.order} 
                    onChange={(e) => setFormData(f => ({ ...f, order: parseInt(e.target.value) || 0 }))}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="0"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-300">Logo (PNG/SVG for transparency)</Label>
                <div className="flex gap-2">
                  <Input 
                    value={formData.logo} 
                    onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white flex-1"
                    placeholder="URL or upload/browse"
                  />
                  <Button 
                    type="button" 
                    size="sm" 
                    variant="outline" 
                    className="border-gray-600"
                    onClick={() => setShowMediaPicker(true)}
                  >
                    <Image className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <label className="cursor-pointer">
                    <input 
                      type="file" 
                      accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                      <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                    </span>
                  </label>
                  {formData.logo && (
                    <img 
                      src={resolveMediaUrl(formData.logo)} 
                      alt="Logo preview" 
                      className="h-10 w-10 object-contain bg-gray-700 rounded p-1"
                    />
                  )}
                </div>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-5 transition-colors" data-testid="save-conference-btn">Save Conference</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={resetForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Conferences grouped by Region */}
      {[...REGIONS, { value: '', label: 'Unassigned' }].map(region => {
        const regionLeagues = leagues.filter(l => (l.region || '') === region.value);
        if (regionLeagues.length === 0) return null;
        return (
          <div key={region.value || 'unassigned'} className="space-y-3" data-testid={`region-group-${region.value || 'unassigned'}`}>
            <h3 className="text-lg font-bold text-white flex items-center gap-2 border-b border-wpsl-blue/30 pb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span className="inline-block w-2 h-6 bg-wpsl-red rounded-sm" />
              <span className="uppercase tracking-wide">WPSL {region.label}</span>
              <span className="text-sm font-normal text-gray-500">({regionLeagues.length})</span>
            </h3>
            <div className="grid gap-3">
              {regionLeagues.sort((a, b) => (a.order || 0) - (b.order || 0)).map(league => (
                <Card key={league.id} className="bg-gray-800/50 border-wpsl-blue/20 hover:border-wpsl-blue/50 transition-colors">
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {league.logo ? (
                        <div className="w-12 h-12 rounded-lg bg-white/10 p-1 flex items-center justify-center">
                          <img src={resolveMediaUrl(league.logo)} alt={league.name} className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-wpsl-blue/20 flex items-center justify-center text-wpsl-blue">
                          <Trophy className="w-6 h-6" />
                        </div>
                      )}
                      <div>
                        <h4 className="text-white font-semibold">{league.name}</h4>
                        <p className="text-sm text-gray-400">/{league.slug} • Order: {league.order || 0}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(league)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="outline" className="border-red-600 text-red-400 hover:bg-red-900/30" onClick={() => handleDelete(league.id)}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {leagues.length === 0 && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardContent className="p-8 text-center">
            <Trophy className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No conferences yet. Add your first conference to get started.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ===========================================================================
// Teams CSV + Folder Import Dialog
// ===========================================================================
// User picks BOTH a CSV (with `logo` column referencing filenames) AND a folder
// of logo image files. We pre-upload only the logo files whose name appears in
// the CSV to the Media Library, build a filename→URL map, rewrite the CSV's
// `logo` cells to those URLs, and finally POST to /admin/teams/bulk-import.
// External http(s) logo cells and missing-file rows still flow through (the
// backend's `resolve_or_fetch_image_url` handles the rest).
const TeamCsvFolderImportDialog = ({ open, onClose, onResult, leagues = [], sports = [], existingTeams = [] }) => {
  const [csvFile, setCsvFile] = useState(null);
  const [logoFiles, setLogoFiles] = useState([]);
  const [phase, setPhase] = useState('idle'); // idle | review | uploading | importing | done | error
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [errorMsg, setErrorMsg] = useState('');
  // Review phase: list of conferences the CSV references but DB doesn't have yet.
  // Each entry: { slug, region, name } where name is editable by the admin.
  const [pendingConferences, setPendingConferences] = useState([]);
  // Captured between phases so we don't re-parse the CSV.
  const [parsedRows, setParsedRows] = useState([]);

  const setFolderInput = React.useCallback((el) => {
    if (el) {
      el.setAttribute('webkitdirectory', '');
      el.setAttribute('directory', '');
      el.setAttribute('mozdirectory', '');
    }
  }, []);

  // Reset everything when dialog closes
  React.useEffect(() => {
    if (!open) {
      setCsvFile(null);
      setLogoFiles([]);
      setPhase('idle');
      setProgress({ done: 0, total: 0 });
      setErrorMsg('');
      setPendingConferences([]);
      setParsedRows([]);
    }
  }, [open]);

  // Reuse the same parser logic as the original handleCsvUpload (kept lenient).
  const parseCsvForImport = (text) => {
    const lines = text.replace(/\r\n/g, '\n').split('\n').filter(l => l.trim());
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const idx = (k) => headers.indexOf(k);
    const get = (cells, i) => (i !== -1 && cells[i] !== undefined ? cells[i].trim() : undefined);
    const map = {
      name: idx('name'), slug: idx('slug'),
      league_id: idx('league_id'), league_slug: idx('league_slug'),
      region_slug: idx('region_slug'), conference_slug: idx('conference_slug'),
      conference_name: idx('conference_name'),
      short_name: idx('short_name'), logo: idx('logo'),
    };
    if (map.name === -1) throw new Error('CSV must include a "name" column');
    const rows = [];
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split(',').map(c => c.trim());
      rows.push({
        name: cells[map.name],
        slug: get(cells, map.slug),
        league_id: get(cells, map.league_id),
        league_slug: get(cells, map.league_slug),
        region_slug: get(cells, map.region_slug),
        conference_slug: get(cells, map.conference_slug),
        conference_name: get(cells, map.conference_name),
        short_name: get(cells, map.short_name),
        logo: get(cells, map.logo),
      });
    }
    return rows;
  };

  // Compute which selected logo files we'll actually upload at submit time.
  // (No memo needed — we just compute counts inline below for the UI hint.)

  // Helper: humanise a slug into a default conference name ("pacific-coast" → "Pacific Coast")
  const humanise = (slug) => (slug || '').replace(/_/g, '-').split('-')
    .filter(Boolean).map(p => p[0].toUpperCase() + p.slice(1)).join(' ');

  // Step 1: parse the CSV and decide whether we need the review step.
  const handleAnalyze = async () => {
    if (!csvFile) { setErrorMsg('Please choose a CSV file.'); return; }
    setErrorMsg('');
    try {
      const text = await csvFile.text();
      const rows = parseCsvForImport(text);
      if (rows.length === 0) throw new Error('CSV has no data rows.');

      // Find every (region, conference_slug) combo the CSV references that
      // doesn't already exist in the DB.
      const knownPairs = new Set(); // "region|slug" + "|slug"
      for (const l of leagues || []) {
        const slug = (l.slug || '').toLowerCase();
        const region = (l.region || '').toLowerCase();
        knownPairs.add(`${region}|${slug}`);
        knownPairs.add(`|${slug}`); // slug-only fallback
      }
      const missingMap = new Map(); // key=region|slug → {slug, region, name}
      for (const r of rows) {
        const slug = (r.conference_slug || r.league_slug || '').toLowerCase().trim();
        if (!slug || r.league_id) continue;
        const region = (r.region_slug || '').toLowerCase().trim();
        const lookup = `${region}|${slug}`;
        const lookupAny = `|${slug}`;
        if (knownPairs.has(lookup) || knownPairs.has(lookupAny)) continue;
        if (missingMap.has(lookup)) continue;
        missingMap.set(lookup, {
          slug,
          region: region || null,
          name: (r.conference_name && r.conference_name.trim()) || humanise(slug),
        });
      }

      setParsedRows(rows);
      if (missingMap.size > 0) {
        setPendingConferences(Array.from(missingMap.values()));
        setPhase('review');
      } else {
        setPendingConferences([]);
        await runImport(rows, []);
      }
    } catch (err) {
      console.error('CSV parse failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'CSV parse failed');
      setPhase('error');
    }
  };

  // Step 2: confirm + create the new conferences, then run import
  const handleReviewConfirm = async () => {
    // Validate names
    const blank = pendingConferences.find(c => !c.name || !c.name.trim());
    if (blank) { setErrorMsg(`Please give "${blank.slug}" a name (or remove it).`); return; }
    setErrorMsg('');
    setPhase('uploading'); // borrow the uploading phase to show "creating conferences…"

    try {
      // Pick a sport to attach the new conferences to. Prefer the sport already
      // used by existing leagues; fall back to the first sport in the catalogue.
      const sportId = (leagues.find(l => l.sport_id)?.sport_id) || sports[0]?.id;
      if (!sportId) throw new Error('Cannot create conference: no sport configured. Add a sport first.');

      for (const c of pendingConferences) {
        await api.createLeague({
          sport_id: sportId,
          name: c.name.trim(),
          slug: c.slug,
          region: c.region || undefined,
        });
      }
      await runImport(parsedRows, pendingConferences);
    } catch (err) {
      console.error('Conference creation failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'Failed to create conference');
      setPhase('error');
    }
  };

  // Step 3: upload logos + post bulk-import
  const runImport = async (rows, justCreated) => {
    setPhase('uploading');
    setErrorMsg('');
    try {
      // Build set of filenames the CSV references in `logo` column.
      const expected = new Set(
        rows
          .map(r => (r.logo || '').trim())
          .filter(Boolean)
          .filter(l => !/^https?:\/\//i.test(l) && !l.startsWith('/'))
          .map(l => l.toLowerCase())
      );

      // Filter selected logo files to only those the CSV references.
      const filesToUpload = logoFiles.filter(
        f => f.type.startsWith('image/') && expected.has(f.name.toLowerCase())
      );

      // Upload each in parallel batches of 5; build name→url map.
      const filenameToUrl = {};
      setProgress({ done: 0, total: filesToUpload.length });
      const concurrency = 5;
      let i = 0;
      while (i < filesToUpload.length) {
        const batch = filesToUpload.slice(i, i + concurrency);
        // eslint-disable-next-line no-loop-func
        await Promise.all(batch.map(async (file) => {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('alt_text', file.name);
          fd.append('tags', 'team-logo');
          try {
            const res = await api.uploadMedia(fd);
            filenameToUrl[file.name.toLowerCase()] = res.data.url;
          } catch (err) {
            console.error('Logo upload failed for', file.name, err);
          } finally {
            setProgress(p => ({ done: p.done + 1, total: p.total }));
          }
        }));
        i += concurrency;
      }

      const rewritten = rows.map(r => {
        if (!r.logo) return r;
        const key = r.logo.toLowerCase();
        return filenameToUrl[key] ? { ...r, logo: filenameToUrl[key] } : r;
      });

      // Wizard pre-creates conferences itself, so disable backend auto-create.
      setPhase('importing');
      const res = await api.bulkImportTeamsRich(rewritten, false);
      setPhase('done');
      const result = {
        ...res.data,
        _uploadedLogos: Object.keys(filenameToUrl).length,
        // Surface conferences the wizard pre-created (fallback to backend's list).
        created_leagues: (justCreated && justCreated.length)
          ? justCreated.map(c => ({ slug: c.slug, region: c.region, name: c.name }))
          : (res.data.created_leagues || []),
      };
      onResult(result);
      onClose();
    } catch (err) {
      console.error('CSV import failed', err);
      setErrorMsg(err.response?.data?.detail || err.message || 'Import failed');
      setPhase('error');
    }
  };

  // Single Submit handler — either kicks off analyze or skips straight ahead
  // depending on phase.
  const handleSubmit = handleAnalyze;

  const expectedHint = (() => {
    if (!csvFile || logoFiles.length === 0) return null;
    // Quick sync read isn't possible; just show counts.
    const imgCount = logoFiles.filter(f => f.type.startsWith('image/')).length;
    return `${imgCount} image file${imgCount === 1 ? '' : 's'} selected. Only files whose names match the CSV "logo" column will be uploaded.`;
  })();

  const busy = phase === 'uploading' || phase === 'importing';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="max-w-2xl bg-gray-900 border-wpsl-blue/40">
        <DialogHeader>
          <DialogTitle className="text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Bulk Import Teams — CSV + Logo Folder
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            Upload a teams CSV alongside a folder of logo images. Logos are matched to CSV rows by exact filename.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-400">
            Drop your <strong className="text-wpsl-blue">teams CSV</strong> and the <strong className="text-wpsl-blue">folder of logo images</strong>.
            Logos are matched to CSV rows by exact filename (case-insensitive).
            External <code className="text-xs bg-gray-800 px-1 rounded">https://…</code> logo URLs are still supported and downloaded by the backend.
            <br/>
            <span className="text-emerald-300">If your CSV references a conference that doesn't exist yet, we'll pause and let you name it</span> before the import runs.
          </p>

          {phase === 'review' ? (
            // ============== REVIEW STEP — confirm/name new conferences ==============
            <div className="space-y-3" data-testid="dual-import-review">
              {(() => {
                const knownTeamSlugs = new Set(
                  (existingTeams || []).map(t => (t.slug || '').toLowerCase())
                );
                let willCreate = 0;
                let willUpdate = 0;
                const seenSlugs = new Set();
                for (const r of parsedRows) {
                  const baseSlug = (r.slug || r.name || '').toLowerCase().trim();
                  // Mirror the backend slug-derivation: lowercase + replace spaces + strip non-alnum
                  const slug = baseSlug.replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
                  if (!slug || seenSlugs.has(slug)) continue;
                  seenSlugs.add(slug);
                  if (knownTeamSlugs.has(slug)) willUpdate += 1;
                  else willCreate += 1;
                }
                const skipped = Math.max(0, parsedRows.length - (willCreate + willUpdate));
                return (
                  <div className="grid grid-cols-3 gap-2" data-testid="dual-import-preview">
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-emerald-300" data-testid="preview-create-count">{willCreate}</div>
                      <div className="text-xs text-gray-400 uppercase">to create</div>
                    </div>
                    <div className="bg-sky-500/10 border border-sky-500/30 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-sky-300" data-testid="preview-update-count">{willUpdate}</div>
                      <div className="text-xs text-gray-400 uppercase">to update</div>
                    </div>
                    <div className="bg-gray-700/50 border border-gray-600/50 rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-gray-300" data-testid="preview-skip-count">{skipped}</div>
                      <div className="text-xs text-gray-400 uppercase">duplicates</div>
                    </div>
                  </div>
                );
              })()}
              <div className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-3 text-sm text-emerald-200">
                Your CSV references <strong>{pendingConferences.length}</strong> conference{pendingConferences.length === 1 ? '' : 's'} that don't exist yet.
                Confirm the name for each — these names will be public on the site, so make sure they're right.
              </div>
              <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                {pendingConferences.map((c, i) => (
                  <div key={c.slug + ':' + (c.region || '')} className="bg-gray-800/60 rounded-lg p-3 border border-gray-700" data-testid={`dual-import-pending-${c.slug}`}>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <code className="text-xs bg-gray-900 px-2 py-0.5 rounded text-sky-300">{c.slug}</code>
                      {c.region && <span className="text-xs text-gray-400">region: <strong className="text-gray-300">{c.region}</strong></span>}
                    </div>
                    <Label className="text-xs text-gray-400">Conference name</Label>
                    <Input
                      type="text"
                      value={c.name}
                      onChange={(e) => {
                        const v = e.target.value;
                        setPendingConferences(prev => prev.map((p, j) => j === i ? { ...p, name: v } : p));
                      }}
                      className="bg-gray-700 border-gray-600 text-white mt-1"
                      data-testid={`dual-import-pending-name-${c.slug}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* CSV file picker */}
              <label className="block cursor-pointer">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors ${csvFile ? 'border-wpsl-blue bg-wpsl-blue/10' : 'border-gray-700 hover:border-wpsl-blue/60'}`}
                  data-testid="dual-import-csv-dropzone"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Step 1 — CSV</p>
                  <p className="text-white font-medium">
                    {csvFile ? csvFile.name : 'Click to choose teams CSV…'}
                  </p>
                  {csvFile && <p className="text-xs text-gray-400">{(csvFile.size / 1024).toFixed(1)} KB</p>}
                </div>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => { setCsvFile(e.target.files?.[0] || null); setPhase('idle'); setErrorMsg(''); }}
                  disabled={busy}
                  data-testid="dual-import-csv-input"
                />
              </label>

              {/* Folder picker */}
              <label className="block cursor-pointer">
                <div
                  className={`border-2 border-dashed rounded-lg p-4 transition-colors ${logoFiles.length > 0 ? 'border-wpsl-blue bg-wpsl-blue/10' : 'border-gray-700 hover:border-wpsl-blue/60'}`}
                  data-testid="dual-import-folder-dropzone"
                >
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Step 2 — Logo folder</p>
                  <p className="text-white font-medium">
                    {logoFiles.length > 0
                      ? `${logoFiles.length} file${logoFiles.length === 1 ? '' : 's'} selected`
                      : 'Click to choose folder of logo images…'}
                  </p>
                  {expectedHint && <p className="text-xs text-gray-400 mt-1">{expectedHint}</p>}
                </div>
                <input
                  ref={setFolderInput}
                  type="file"
                  multiple
                  className="hidden"
                  onChange={(e) => { setLogoFiles(Array.from(e.target.files || [])); setPhase('idle'); setErrorMsg(''); }}
                  disabled={busy}
                  data-testid="dual-import-folder-input"
                />
              </label>
            </>
          )}

          {/* Progress */}
          {phase === 'uploading' && (
            <div className="bg-gray-800/60 rounded-lg p-3 text-sm text-gray-200" data-testid="dual-import-progress">
              {progress.total > 0
                ? <>Uploading logos: <strong className="text-wpsl-blue">{progress.done}</strong> / {progress.total}</>
                : <>Preparing import…</>}
              <div className="mt-2 h-1.5 bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-wpsl-blue transition-all"
                  style={{ width: progress.total ? `${(progress.done / progress.total) * 100}%` : '0%' }}
                />
              </div>
            </div>
          )}
          {phase === 'importing' && (
            <div className="bg-gray-800/60 rounded-lg p-3 text-sm text-gray-200" data-testid="dual-import-progress">
              Importing CSV rows…
            </div>
          )}
          {(phase === 'error' || (phase === 'review' && errorMsg)) && errorMsg && (
            <div className="bg-red-900/30 border border-red-600/50 rounded-lg p-3 text-sm text-red-300" data-testid="dual-import-error">
              {errorMsg}
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            {phase === 'review' && (
              <Button
                variant="outline"
                className="border-gray-600 text-gray-300"
                onClick={() => { setPhase('idle'); setErrorMsg(''); }}
                data-testid="dual-import-back"
              >
                ← Back
              </Button>
            )}
            <Button
              variant="outline"
              className="border-gray-600 text-gray-300"
              onClick={() => { if (!busy) onClose(); }}
              disabled={busy}
              data-testid="dual-import-cancel"
            >
              Cancel
            </Button>
            <Button
              className="bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-5"
              onClick={phase === 'review' ? handleReviewConfirm : handleSubmit}
              disabled={busy || (phase !== 'review' && !csvFile)}
              data-testid="dual-import-submit"
            >
              {phase === 'uploading' ? (progress.total > 0 ? 'Uploading logos…' : 'Creating conferences…')
                : phase === 'importing' ? 'Importing…'
                : phase === 'review' ? `Confirm & Import (${pendingConferences.length} new)`
                : 'Upload & Import'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};


// Admin Teams
const AdminTeams = ({ teams: initialTeams, leagues, sports, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [formData, setFormData] = useState({ league_ids: [], name: '', slug: '', logo: '', short_name: '' });
  const [uploading, setUploading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);

  // Paginated, searchable teams listing
  const [teams, setTeams] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [leagueFilter, setLeagueFilter] = useState('');
  const [loadingTeams, setLoadingTeams] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchTeams = useCallback(async () => {
    setLoadingTeams(true);
    try {
      const res = await api.listAdminTeams({ page, per_page: perPage, search, league_id: leagueFilter });
      setTeams(res.data.items || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 0);
    } catch (err) {
      console.error('Failed to load teams', err);
    } finally {
      setLoadingTeams(false);
    }
  }, [page, perPage, search, leagueFilter]);

  useEffect(() => { fetchTeams(); }, [fetchTeams]);

  const refreshTeams = useCallback(async () => {
    await fetchTeams();
    if (onRefresh) onRefresh();
  }, [fetchTeams, onRefresh]);

  // Multi-select bulk actions
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const allSelected = teams.length > 0 && selectedIds.size === teams.length;
  const someSelected = selectedIds.size > 0 && !allSelected;

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(teams.map(t => t.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (kind) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      if (kind === 'delete') {
        if (!window.confirm(`Permanently delete ${ids.length} team${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
        const res = await api.bulkDeleteTeams(ids);
        alert(`${res.data.deleted} team(s) deleted.`);
      } else {
        const action = kind === 'assign' ? 'add to' : 'move to';
        const lid = window.prompt(
          `Enter the conference SLUG to ${action} (e.g. west, east, central, south):\n\n` +
          `Available: ${leagues.map(l => l.slug).join(', ')}`
        );
        if (!lid) return;
        const league = leagues.find(l => l.slug === lid.trim().toLowerCase());
        if (!league) {
          alert(`No conference with slug "${lid}" found.`);
          return;
        }
        if (kind === 'move') {
          if (!window.confirm(`Move ${ids.length} team(s) to "${league.name}"? This will REPLACE their existing conference assignments.`)) return;
          const res = await api.bulkMoveTeamsConference(ids, league.id);
          alert(`${res.data.modified} team(s) moved to ${league.name}.`);
        } else {
          const res = await api.bulkAssignTeamsConference(ids, league.id);
          alert(`${res.data.modified} team(s) added to ${league.name}.`);
        }
      }
      clearSelection();
      refreshTeams();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Unknown error';
      alert(`Bulk action failed: ${detail}`);
      console.error(err);
    } finally {
      setBulkBusy(false);
    }
  };

  // CSV bulk import is now handled inside <TeamCsvFolderImportDialog />
  // (see component above). The dialog parses the CSV, pre-uploads matching
  // logo files to the Media Library, rewrites rows, then POSTs bulk-import.

  const downloadCsvTemplate = () => {
    const sampleRegion = leagues[0]?.region || 'west';
    const sampleConf = leagues[0]?.slug || sampleRegion;
    // `logo` accepts either a full http(s) URL OR a bare filename that already lives
    // in the Media Library (use "Upload Folder" in the Media tab to bulk-upload first).
    // If `conference_slug` doesn't already exist, the import auto-creates the
    // conference. `conference_name` is optional — if omitted, the slug is humanised.
    const csv = [
      'name,region_slug,conference_slug,conference_name,short_name,logo',
      `Example FC,${sampleRegion},${sampleConf},,EFC,example-fc.png`,
      `Sample United,${sampleRegion},${sampleConf},,SU,https://example.com/sample-united.png`,
      // Demo of auto-created conference: Pacific Coast doesn't exist yet
      `Pacific Coast Demo FC,west,pacific-coast,Pacific Coast,PCD,`,
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'wpsltv_teams_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const [exporting, setExporting] = useState(false);
  const exportTeamsCsv = async () => {
    setExporting(true);
    try {
      const res = await api.exportTeamsCsv();
      const url = URL.createObjectURL(res.data);
      const stamp = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = url;
      a.download = `wpsltv_teams_${stamp}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Export failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // Handle logo file upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type (PNG or SVG for transparency)
    const validTypes = ['image/png', 'image/svg+xml', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Please upload a PNG, SVG, or WebP file for transparent logos');
      return;
    }
    
    setUploading(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      uploadFormData.append('alt_text', `${formData.name || 'Team'} logo`);
      uploadFormData.append('tags', 'logo,team');
      
      const res = await axios.post(`${API}/admin/media/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Set the uploaded URL as the logo
      const logoUrl = resolveMediaUrl(res.data.url);
      setFormData({ ...formData, logo: logoUrl });
    } catch (error) {
      alert('Error uploading logo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  const toggleLeague = (leagueId) => {
    setFormData(prev => ({
      ...prev,
      league_ids: prev.league_ids.includes(leagueId)
        ? prev.league_ids.filter(id => id !== leagueId)
        : [...prev.league_ids, leagueId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTeam) {
        await api.updateTeam(editingTeam.id, formData);
      } else {
        await api.createTeam(formData);
      }
      setShowForm(false);
      setEditingTeam(null);
      setFormData({ league_ids: [], name: '', slug: '', logo: '', short_name: '' });
      refreshTeams();
    } catch (error) {
      const detail = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error saving team: ${detail}`);
      console.error('Save team failed:', error);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    // Support both old league_id and new league_ids format
    const leagueIds = team.league_ids || (team.league_id ? [team.league_id] : []);
    setFormData({ ...team, league_ids: leagueIds });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      try {
        await api.deleteTeam(id);
        refreshTeams();
      } catch (error) {
        const detail = error.response?.data?.detail || error.message || 'Unknown error';
        alert(`Error deleting team: ${detail}`);
        console.error('Delete team failed:', error);
      }
    }
  };

  const getLeagueName = (leagueId) => leagues.find(l => l.id === leagueId)?.name || null;
  const getSportForLeague = (leagueId) => {
    const league = leagues.find(l => l.id === leagueId);
    return sports.find(s => s.id === league?.sport_id);
  };
  
  // Get all league names for a team (supports both old and new format)
  const getTeamLeagues = (team) => {
    const ids = team.league_ids || (team.league_id ? [team.league_id] : []);
    const names = ids.map(id => getLeagueName(id)).filter(Boolean);
    return names.length > 0 ? names.join(', ') : 'No conference assigned';
  };

  return (
    <div className="space-y-6">
      <TeamCsvFolderImportDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onResult={(result) => { setImportResult(result); refreshTeams(); }}
        leagues={leagues}
        sports={sports}
        existingTeams={teams}
      />
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          Teams <span className="text-gray-400 text-base font-normal">({total})</span>
        </h2>
        <div className="flex gap-2 items-center">
          <Button
            onClick={downloadCsvTemplate}
            variant="outline"
            className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
            data-testid="download-csv-template"
          >
            CSV Template
          </Button>
          <Button
            onClick={exportTeamsCsv}
            variant="outline"
            disabled={exporting || teams.length === 0}
            className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
            data-testid="export-teams-csv"
          >
            {exporting ? 'Exporting…' : 'Export Teams CSV'}
          </Button>
          <Button
            onClick={() => setShowImportDialog(true)}
            variant="outline"
            disabled={importing}
            className="border-wpsl-blue bg-wpsl-blue/10 text-wpsl-blue hover:bg-wpsl-blue hover:text-white"
            data-testid="csv-upload-btn"
          >
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing…' : 'Bulk Import CSV'}
          </Button>
          <Button
            onClick={() => { setShowForm(true); setEditingTeam(null); setFormData({ league_ids: [], name: '', slug: '', logo: '', short_name: '' }); }}
            className="bg-wpsl-red hover:bg-wpsl-blue text-white rounded-full px-5 transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Team
          </Button>
        </div>
      </div>

      {/* Search + filters + pagination controls */}
      <div className="flex flex-wrap gap-2 items-center bg-gray-800/40 border border-gray-700/50 rounded-lg p-3">
        <div className="flex-1 min-w-[220px]">
          <Input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search teams by name, slug, or short name…"
            className="bg-gray-700 border-gray-600 text-white"
            data-testid="teams-search-input"
          />
        </div>
        <Select value={leagueFilter || 'all'} onValueChange={(v) => { setLeagueFilter(v === 'all' ? '' : v); setPage(1); }}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white w-[220px]" data-testid="teams-league-filter">
            <SelectValue placeholder="All conferences" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all" className="text-white">All conferences</SelectItem>
            {leagues.map(l => (
              <SelectItem key={l.id} value={l.id} className="text-white">{l.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(perPage)} onValueChange={(v) => { setPerPage(parseInt(v, 10)); setPage(1); }}>
          <SelectTrigger className="bg-gray-700 border-gray-600 text-white w-[110px]" data-testid="teams-per-page">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            {[25, 50, 100, 200].map(n => (
              <SelectItem key={n} value={String(n)} className="text-white">{n} / page</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-1 ml-auto">
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1 || loadingTeams}
            data-testid="teams-prev-page"
          >← Prev</Button>
          <span className="text-xs text-gray-300 px-2 min-w-[80px] text-center" data-testid="teams-page-indicator">
            Page {page} of {Math.max(1, pages)}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="border-gray-600 text-gray-300"
            onClick={() => setPage(p => Math.min(pages || 1, p + 1))}
            disabled={page >= pages || loadingTeams}
            data-testid="teams-next-page"
          >Next →</Button>
        </div>
      </div>

      {importResult && (
        <Card className={`border ${importResult.error ? 'border-red-600/50 bg-red-900/20' : 'border-wpsl-blue/40 bg-wpsl-blue/10'}`} data-testid="csv-import-result">
          <CardContent className="p-4 text-sm">
            {importResult.error ? (
              <p className="text-red-300">Import failed: {importResult.error}</p>
            ) : (
              <div className="text-gray-200">
                <p>
                  <strong className="text-wpsl-blue">{importResult.created}</strong> created,{' '}
                  <strong className="text-wpsl-blue">{importResult.updated}</strong> updated (merged with existing),{' '}
                  <strong className={importResult.errors?.length ? 'text-red-300' : 'text-gray-400'}>
                    {importResult.errors?.length || 0}
                  </strong>{' '}
                  failed — out of {importResult.total} rows.
                </p>
                {typeof importResult._uploadedLogos === 'number' && importResult._uploadedLogos > 0 && (
                  <p className="text-xs text-wpsl-blue mt-1">
                    {importResult._uploadedLogos} logo file{importResult._uploadedLogos === 1 ? '' : 's'} uploaded to Media Library.
                  </p>
                )}
                {Array.isArray(importResult.created_leagues) && importResult.created_leagues.length > 0 && (
                  <p className="text-xs text-emerald-300 mt-1">
                    Auto-created {importResult.created_leagues.length} new conference{importResult.created_leagues.length === 1 ? '' : 's'}: {importResult.created_leagues.map(l => l.slug).join(', ')}
                  </p>
                )}
                {importResult.errors?.length > 0 && (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-red-300">View errors</summary>
                    <ul className="mt-2 ml-4 text-xs text-red-300 list-disc">
                      {importResult.errors.map((e, i) => (
                        <li key={i}>Row {e.row}{e.name ? ` (${e.name})` : ''}: {e.error}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
            <button onClick={() => setImportResult(null)} className="mt-2 text-xs text-gray-500 hover:text-gray-300">Dismiss</button>
          </CardContent>
        </Card>
      )}

      {showForm && (
        <Card className="bg-gray-800/50 border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-white">{editingTeam ? 'Edit Team' : 'Add New Team'}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label className="text-gray-300">Conferences</Label>
                  <p className="text-gray-500 text-xs mb-2">Select all conferences this team plays in</p>
                  <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto bg-gray-900/50 p-3 rounded-lg">
                    {leagues.map(league => (
                      <label 
                        key={league.id} 
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          formData.league_ids.includes(league.id) 
                            ? 'bg-sky-600/30 border border-sky-500' 
                            : 'bg-gray-700/50 hover:bg-gray-700 border border-transparent'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.league_ids.includes(league.id)}
                          onChange={() => toggleLeague(league.id)}
                          className="rounded border-gray-500"
                        />
                        <span className="text-white text-sm flex items-center gap-1">
                          <SportIcon sport={getSportForLeague(league.id)} className="w-3 h-3" /> {league.name}
                        </span>
                      </label>
                    ))}
                  </div>
                  {formData.league_ids.length > 0 && (
                    <p className="text-sky-400 text-xs mt-1">{formData.league_ids.length} conference(s) selected</p>
                  )}
                </div>
                <div>
                  <Label className="text-gray-300">Team Name</Label>
                  <Input 
                    value={formData.name} 
                    onChange={(e) => setFormData({ ...formData, name: e.target.value, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="Team Name"
                    required
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Short Name</Label>
                  <Input 
                    value={formData.short_name} 
                    onChange={(e) => setFormData({ ...formData, short_name: e.target.value })}
                    className="bg-gray-700 border-gray-600 text-white"
                    placeholder="e.g., NYC"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Logo (PNG/SVG for transparency)</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={formData.logo} 
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                      className="bg-gray-700 border-gray-600 text-white flex-1"
                      placeholder="URL or upload/browse"
                    />
                    <Button 
                      type="button" 
                      size="sm" 
                      variant="outline" 
                      className="border-gray-600"
                      onClick={() => setShowMediaPicker(true)}
                    >
                      <Image className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <label className="cursor-pointer">
                      <input 
                        type="file" 
                        accept=".png,.svg,.webp,image/png,image/svg+xml,image/webp"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-sm ${uploading ? 'bg-gray-600 text-gray-400' : 'bg-sky-600 hover:bg-sky-700 text-white'}`}>
                        <Upload className="w-4 h-4" /> {uploading ? 'Uploading...' : 'Upload New'}
                      </span>
                    </label>
                    {formData.logo && (
                      <img 
                        src={resolveMediaUrl(formData.logo)} 
                        alt="Logo preview" 
                        className="h-10 w-10 object-contain bg-gray-700 rounded p-1"
                        style={{ imageRendering: 'crisp-edges' }}
                      />
                    )}
                  </div>
                </div>
              </div>
              
              <MediaPickerDialog
                open={showMediaPicker}
                onOpenChange={setShowMediaPicker}
                onSelect={(url) => setFormData({ ...formData, logo: url })}
              />
              
              <div className="flex gap-2">
                <Button type="submit" className="bg-sky-300 hover:bg-sky-400">Save Team</Button>
                <Button type="button" variant="outline" className="border-gray-600" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-4">
        {/* Multi-select header (Select All + bulk-action toolbar) */}
        {teams.length > 0 && (
          <div
            className={`sticky top-0 z-10 -mx-2 px-3 py-2 rounded-lg border flex flex-wrap items-center justify-between gap-3 transition-colors ${
              selectedIds.size > 0
                ? 'bg-wpsl-blue/20 border-wpsl-blue/60'
                : 'bg-gray-800/60 border-gray-700'
            }`}
            data-testid="teams-bulk-toolbar"
          >
            <label className="flex items-center gap-2 text-white text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={allSelected}
                ref={el => { if (el) el.indeterminate = someSelected; }}
                onChange={toggleAll}
                className="w-4 h-4 accent-wpsl-blue"
                data-testid="teams-select-all"
              />
              <span className="font-medium">
                {selectedIds.size === 0
                  ? `Select all ${teams.length} team${teams.length === 1 ? '' : 's'}`
                  : `${selectedIds.size} of ${teams.length} selected`}
              </span>
            </label>
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="border-wpsl-blue/50 text-wpsl-blue hover:bg-wpsl-blue/10"
                  disabled={bulkBusy}
                  onClick={() => runBulk('assign')}
                  data-testid="teams-bulk-assign"
                  title="Add the selected teams to a conference (additive)"
                >
                  Add to Conference
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                  disabled={bulkBusy}
                  onClick={() => runBulk('move')}
                  data-testid="teams-bulk-move"
                  title="Replace the selected teams' conference assignments"
                >
                  Move to Conference
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={bulkBusy}
                  onClick={() => runBulk('delete')}
                  data-testid="teams-bulk-delete"
                >
                  <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedIds.size}
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-gray-300 hover:bg-gray-700"
                  onClick={clearSelection}
                  data-testid="teams-bulk-clear"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}

        {teams.map(team => {
          const checked = selectedIds.has(team.id);
          return (
          <Card key={team.id} className={`bg-gray-800/50 border-sky-900/30 ${checked ? 'ring-2 ring-wpsl-blue' : ''}`}>
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleOne(team.id)}
                  className="w-4 h-4 accent-wpsl-blue cursor-pointer"
                  data-testid={`teams-select-${team.id}`}
                  aria-label={`Select ${team.name}`}
                />
                {team.logo ? (
                  <div className="w-12 h-12 rounded-lg bg-white/10 p-1 flex items-center justify-center">
                    <img src={resolveMediaUrl(team.logo)} alt={team.name} className="w-full h-full object-contain" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-gray-400">
                    <Users className="w-6 h-6" />
                  </div>
                )}
                <div>
                  <h3 className="text-white font-semibold">{team.name} {team.short_name && `(${team.short_name})`}</h3>
                  <p className="text-sm text-sky-200">{getTeamLeagues(team)}</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="border-gray-600" onClick={() => handleEdit(team)}>
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="destructive" onClick={() => handleDelete(team.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
          );
        })}
        {teams.length === 0 && (
          <div className="text-center py-8 text-gray-400" data-testid="teams-empty-state">
            {loadingTeams
              ? 'Loading teams…'
              : (search || leagueFilter)
                ? 'No teams match your search. Try a different keyword or clear the filter.'
                : 'No teams yet. Add your first team!'}
          </div>
        )}
      </div>
    </div>
  );
};

// Admin Games

export { AdminPartners, AdminPageBanners, AdminLeagues, AdminTeams };
